import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import LabShell from '../orders/LabShell';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AlertTriangle, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  imported: { label: 'Importée', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  matched: { label: 'Rapprochée', cls: 'bg-green-50 text-green-700 border-green-200' },
  partial: { label: 'Partielle', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  error: { label: 'Erreur', cls: 'bg-red-50 text-red-700 border-red-200' },
};

export default function LabInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/supplier-invoices`);
      setInvoices(data);
    } catch (e) {
      toast.error('Erreur de chargement');
    }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const upload = async () => {
    if (!file || !invoiceNumber) { toast.error('Fichier et n° de facture requis'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('invoice_number', invoiceNumber);
      if (invoiceDate) fd.append('invoice_date', invoiceDate);
      const { data } = await axios.post(`${API}/supplier-invoices`, fd);
      toast.success(`Facture déposée — ${STATUS[data.status]?.label}`);
      setFile(null); setInvoiceNumber(''); setInvoiceDate('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import");
    } finally { setBusy(false); }
  };

  return (
    <LabShell title="Factures fournisseur">
      <Card className="border-border mb-4">
        <CardHeader><CardTitle className="text-base">Déposer une facture (Excel/CSV)</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-4 gap-3 items-end">
          <div>
            <Label>N° facture</Label>
            <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} data-testid="lab-inv-number-input" />
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
          </div>
          <div>
            <Label>Fichier</Label>
            <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files[0])} data-testid="lab-inv-file-input" />
          </div>
          <Button onClick={upload} disabled={busy} className="gap-2" data-testid="lab-inv-upload-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Déposer
          </Button>
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground mb-3">
        ⚠ Chaque ligne de votre facture doit mentionner le numéro de dossier JPD (ex : JPD-2026-00000001)
        figurant sur le bon de travail, pour un rapprochement automatique.
      </p>
      <div className="space-y-2">
        {invoices.map((inv) => {
          const s = STATUS[inv.status] || STATUS.imported;
          const matched = inv.parsed_lines.filter((l) => l.matched_order_id).length;
          return (
            <Card key={inv.id} className="border-border" data-testid={`lab-inv-row-${inv.invoice_number}`}>
              <CardContent className="py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-sm">{inv.invoice_number}</p>
                    <p className="text-xs text-muted-foreground">{matched}/{inv.parsed_lines.length} ligne(s) rapprochée(s) · {inv.created_at?.slice(0, 10)}</p>
                  </div>
                  <Badge variant="outline" className={s.cls}>{s.label}</Badge>
                </div>
                {inv.alerts?.length > 0 && inv.alerts.map((a, i) => (
                  <p key={i} className="mt-1 text-xs text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 shrink-0" />{a.message}
                  </p>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </LabShell>
  );
}
